import { Skeleton } from '../../../../ds/components';
import { useSavedPill } from '../../SettingsFrame';
import { useBrand, useCompany } from '../../../onboarding/admin/api';
import { ColorSwatches, LogoSlots, PortalPreview, brandStyles } from '../../../onboarding/admin/BrandParts';
import { useCompanyForm } from '../../../onboarding/admin/companyForm';
import { LoadError } from '../../../onboarding/admin/LoadError';
import { useSaveColor } from '../../../onboarding/admin/steps/BrandStep';
import type { Company, CompanyField } from '../../../onboarding/admin/types';
import { WizardField } from '../../../onboarding/ui';
import { OrgSettingsPage } from './OrgSettingsPage';
import styles from './BrandSettingsPage.module.css';

/** Einstellungen 20 · Marke: logo slots, sender and address (auto-save per field), accent colour, portal preview. */
export function BrandSettingsPage() {
  const { markSaved } = useSavedPill();
  const brand = useBrand();
  const company = useCompany();
  const color = useSaveColor(markSaved);
  const error = brand.error ?? company.error;
  return (
    <OrgSettingsPage
      tabs
      gap={24}
      title="Marke"
      description="Logo, Farbe und Absenderdaten erscheinen im Mandantenportal, in Exposés und in jeder ausgehenden Mail."
    >
      {brand.isPending || company.isPending ? (
        <div className={styles.grid}>
          <Skeleton variant="block" height={520} />
          <Skeleton variant="block" height={420} />
        </div>
      ) : error ? (
        <LoadError
          what="Die Marke konnte nicht geladen werden"
          error={error}
          retrying={brand.isFetching || company.isFetching}
          onRetry={() => {
            void brand.refetch();
            void company.refetch();
          }}
        />
      ) : brand.data && company.data ? (
        <div className={styles.grid}>
          <div className={styles.column}>
            <section className={styles.card} aria-labelledby="brand-logo">
              <h3 id="brand-logo" className={styles.cardTitle}>
                Logo
              </h3>
              <LogoSlots brand={brand.data} variant="settings" onChanged={markSaved} />
              <span className={brandStyles.rule}>
                SVG oder PNG mit transparentem Hintergrund, mindestens 600 px Breite.
              </span>
            </section>
            <SenderCard company={company.data} onSaved={markSaved} />
          </div>
          <div className={styles.column}>
            <section className={`${styles.card} ${styles.cardRight}`} aria-labelledby="brand-color">
              <h3 id="brand-color" className={`${styles.cardTitle} ${styles.cardTitleSmall}`}>
                Akzentfarbe
              </h3>
              <ColorSwatches
                presets={brand.data.colorPresets}
                value={brand.data.color}
                compact
                onPick={(value) => void color.save(value)}
              />
              <span className={styles.note}>
                Wird für Schaltflächen und Hervorhebungen im Mandantenportal verwendet.
              </span>
              {color.error ? (
                <span className={styles.error} role="alert">
                  {color.error}
                </span>
              ) : null}
            </section>
            <section
              className={`${styles.card} ${styles.cardRight} ${styles.cardPreview}`}
              aria-labelledby="brand-preview"
            >
              <h3 id="brand-preview" className={`${styles.cardTitle} ${styles.cardTitleSmall}`}>
                Vorschau · Mandantenportal
              </h3>
              <PortalPreview brand={brand.data} color={brand.data.color} variant="settings" />
            </section>
          </div>
        </div>
      ) : null}
    </OrgSettingsPage>
  );
}

const senderFields: { field: CompanyField; label: string; type?: string; numeric?: boolean }[] = [
  { field: 'companyName', label: 'Firmenname' },
  { field: 'senderName', label: 'Absendername für Mails' },
  { field: 'street', label: 'Straße und Hausnummer' },
  { field: 'postalCodeCity', label: 'PLZ und Ort', numeric: true },
  { field: 'register', label: 'Registergericht und HRB' },
  { field: 'vatId', label: 'Umsatzsteuer-ID', numeric: true },
  { field: 'phone', label: 'Telefon', type: 'tel' },
  { field: 'contactEmail', label: 'E-Mail', type: 'email' },
];

/** „Absender und Anschrift" with the two own fields Telefon/E-Mail (the portal login's organization line, D-127). */
function SenderCard({ company, onSaved }: { company: Company; onSaved: () => void }) {
  const form = useCompanyForm(company, onSaved);
  return (
    <section className={styles.card} aria-labelledby="brand-sender">
      <h3 id="brand-sender" className={styles.cardTitle}>
        Absender und Anschrift
      </h3>
      <div className={styles.fields}>
        {senderFields.map(({ field, label, type, numeric }) => (
          <WizardField
            key={field}
            dense
            label={label}
            type={type ?? 'text'}
            numeric={numeric}
            value={form.values[field]}
            onChange={(event) => form.set(field, event.target.value)}
            onBlur={() => void form.commit(field)}
            error={
              form.errors[field] ??
              (field === 'vatId' && company.vatCheck.status === 'invalid'
                ? company.vatCheck.label
                : undefined)
            }
          />
        ))}
      </div>
      {form.failure ? (
        <span className={styles.error} role="alert">
          {form.failure}
        </span>
      ) : null}
    </section>
  );
}
